/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import KeyboardArrowLeftTwoTone from '@material-ui/icons/KeyboardArrowLeftTwoTone';
import classnames from 'classnames';
import styled from 'styled-components';
import {translate} from '@ion/core/src/locale/locale';
import {BaseAsset, Crumb, SCDeviceType} from '@ion/core/src/types/entities';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {isNonZeroNumber} from '@ion/core/src/utils/NumberUtil';
import {hexToRgb} from '@ion/core/src/utils/StringUtil';
import {isCurrentTheme, useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import useRootManager from '../../client/useRootManager';
import {
  IconButton
} from '../../components/buttons/IconButton';
import ChargeLevel from '../../components/chargelevel/ChargeLevel';
import Tooltip from '../../components/tooltip/Tooltip';
import {TooltipWrapper} from '../../components/tooltip/TooltipWrapper';
import {AssetStatusIcon} from '../../icons/AssetStatusIcon';
import {MapLocateIcon} from '../../icons/MapLocateIcon';
import {MapPinIcon} from '../../icons/MapPinIcon';
import {PoorGpsIcon} from '../../icons/PoorGpsIcon';
import {SystemDateTimeIcon} from '../../icons/SystemDateTimeIcon';
import {SystemFindNearestIcon} from '../../icons/SystemFindNearestIcon';
import {SystemViewHistoryIcon} from '../../icons/SystemViewHistoryIcon';
import Render from '../../render/Render';
import {
  dlsAlt,
  dlsColor,
  dlsLayout,
  dlsTypography
} from '../../theme/theme';
import {
  BodyTypographyVariantEnum,
  DLSTheme
} from '../../types/types';
import {
  BodyTypography
} from '../../typography/BodyTypography';
import {
  LabelTypography
} from '../../typography/LabelTypography';
import {formatAddress, getFormattedDate} from '../../utils/ComponentUtils';
import {navigateToGoogleDirections, navigateToGoogleStreetView, NavigationView} from '../helpers/globeUtils';
import {getDeviceStatus, getSCStatus} from '../helpers/MapViewUtils';
import useIsHistoryAvailable from '../hooks/useIsHistoryAvailable';
import {FindNearbyType} from '../layers/AssetLayer';
import FindNearby from './findnearby/FindNearby';
import Address from './Address';
import sharedStyles from './sharedStyles';

// Defines SC Device Props
export interface SCDeviceProps {
  selectedDevice: SCDeviceType,
  setSelectedDevice: Function,
  crumbs: Crumb[],
  setCrumbs: Function,
  setDeviceDetailView: Function,
  setFindButtonStatus: Function,
  isHistoryAccessible?: boolean,
  assetFindNearbyClick: Function
  isFindNearbyPopup?: boolean,
  onFindNearyBy?: Function,
  onFindNearbyBreadcrumbBackClick?: Function,
  onFindNearbyPopupStatusChange?: Function,
  onFindButtonStateChange?: Function,
  findLabel?: FindNearbyType,
  mapView?: __esri.MapView;
  onDeviceReports?: Function;
};

const StyledCenterContent = styled.div`
  display     : flex;
  align-items : center;
`;

interface StyleProps {
  showFindNearBy: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  const margin = dlsLayout('spacing') * 2;
  const spacing = dlsLayout('spacing');
  return {
    container : {
      margin        : '0',
      cursor        : 'pointer',
      overflow      : 'hidden',
      display       : 'flex',
      flexDirection : 'row'
    },
    contentContainer : {
      width : '87%'
    },
    arrow : {
      color : dlsColor('grey1')
    },
    tooltip : {
      ...dlsAlt('components', 'addressTooltip')
    },
    centered : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      gap            : 6
    },
    actionContainer : {
      width           : '13%',
      backgroundColor : dlsColor('grey6')
    },
    ctaContainer : {
      display        : 'flex',
      flexDirection  : 'column',
      height         : ({showFindNearBy}: StyleProps) => showFindNearBy ? '87%' : '100%',
      justifyContent : 'space-evenly',
      gap            : 4
    },
    statusContainer : {
      display        : 'flex',
      flexDirection  : 'row',
      justifyContent : 'space-between',
      alignItems     : 'center',
      height         : 26,
      paddingRight   : 5,
      paddingLeft    : 5
    },
    batteryContent : {
      height     : 'inherit',
      display    : 'flex',
      alignItems : 'center',
      gap        : 3,
      color      : dlsColor('white')
    },
    fontContent : {
      fontWeight : 700,
      fontSize   : 12,
      lineHeight : '16px'
    },
    detailContainer : {
      maxHeight     : 120,
      height        : 100,
      display       : 'flex',
      flexDirection : 'column',
      alignItems    : 'flex-start',
      marginBottom  : 6
    },
    deviceNameContainer : {
      color         : dlsColor('aaBlue'),
      textTransform : 'uppercase',
      height        : 24,
      lineHeight    : '24px !important',
      marginLeft    : '9px'
    },
    subText : {
      color         : dlsColor('grey3'),
      margin        : '0 !important',
      padding       : '0 0 2px !important',
      fontSize      : '10px !important',
      fontWeight    : 500,
      textTransform : 'uppercase',
      marginLeft    : '13px !important'
    },
    eventTime : {
      fontSize   : '11px !important',
      fontWeight : 700,
      color      : dlsColor('grey2'),
      marginLeft : '10px !important'
    },
    addressContainer : {
      display    : 'flex',
      alignItems : 'center',
      marginLeft : 12
    },
    CTAlabel : {
      height : '22px !important',
      width  : '22px !important'
    },
    nameLabel : {
      whiteSpace   : 'nowrap',
      overflow     : 'hidden',
      textOverflow : 'ellipsis'
    },
    nonPadding : {
      padding : '0px !important'
    },
    labelContainer : {
      padding      : '0px !important',
      marginTop    : '-2px',
      marginBottom : '3px'
    },
    label : {
      color : dlsColor('grey1'),
      ...dlsAlt('typography', 'cta2')
    },
    text : {
      ...dlsAlt('typography', 'label2')
    },
    contentWrapper : {
      marginTop    : margin,
      marginRight  : -spacing,
      overflowY    : 'auto',
      paddingRight : spacing
    },
    navigateTo : {
      width          : '100%',
      display        : 'flex',
      justifyContent : 'space-evenly',
      alignItems     : 'center',
      marginTop      : spacing
    },
    navigateToBtn : {
      maxWidth            : '69px',
      padding             : '6px 0px',
      '& .MuiButton-root' : {
        padding  : '0px',
        maxWidth : '69px'
      },
      '& .MuiButton-label' : {
        fontSize : '11px'
      }
    },
    altStatusItemTypography : {
      textAlign : 'right',
      ...dlsAlt('typography', 'label2')
    },
    dateTime : {
      ...dlsTypography('slider'),
      color          : dlsColor('grey3'),
      display        : 'flex',
      justifyContent : 'flex-end',
      marginBottom   : '6px'
    },
    status : {
      paddingLeft : '5px',
      width       : '220px'
    },
    findNearby : {
      paddingBottom : '10px'
    },
    hideText : {
      visibility : 'hidden'
    },
    iconSpan : {
      display     : 'flex',
      position    : 'relative',
      top         : 1,
      paddingLeft : 2
    },
    ...sharedStyles(theme)
  };
});

/**
 * Reusable component for displaying SC Device Popup
 *
 * ```typescript
 *
 *   <SCDevicePopup selectedDevice={device}
 *     crumbs={crumbs}
 *     setDeviceDetailView={setDeviceDetailView}
 *     ....
 *   />
 *
 * ```
 */
const SCDevicePopup = ({
  selectedDevice,
  setDeviceDetailView,
  setSelectedDevice,
  isHistoryAccessible,
  setFindButtonStatus,
  isFindNearbyPopup,
  onFindNearyBy,
  onFindNearbyPopupStatusChange,
  onFindButtonStateChange,
  findLabel,
  mapView,
  assetFindNearbyClick,
  onDeviceReports
}: SCDeviceProps) => {
  const [showFindNearBy, setShowFindNearBy]         = useState(false);
  const [showHistoryToolTip, setShowHistoryToolTip] = useState(false);
  const {getContainer}                              = useRootManager(false);
  const {mode}                                      = useThemeMode();

  // Define the Pervent Default event
  const preventDefaultEvent = useCallback(e => {
    e?.stopPropagation();
    e?.preventDefault();
  }, []);

  useEffect(() => {
    return () => {
      setShowHistoryToolTip(false);
    };
  }, []);

  //Defines the Details click
  const onDetailClick = useCallback((
    e: MouseEvent<HTMLSpanElement>,
    device: SCDeviceType
  ) => {
    const deviceData = {
      ...device,
      isScDevice : true,
      isCvsAsset : false
    };
    setFindButtonStatus(false);
    setSelectedDevice(deviceData, true);
    setDeviceDetailView();
  }, []);

  // Defines the Details event
  const detailView = useCallback(e => {
    onDetailClick(e, selectedDevice);
  }, [selectedDevice]);

  // Defines the Find Nearby event
  const onFindNearbyClick = useCallback(function(e) {
    preventDefaultEvent(e);
    assetFindNearbyClick(selectedDevice);
    setShowFindNearBy(false);
    mapView?.popup.close();
  }, [selectedDevice, mapView, assetFindNearbyClick]);

  const toggleFindNearbyContent = useCallback((show) => {
    return function(e) {
      preventDefaultEvent(e);
      setShowFindNearBy(show);
    };
  }, []);

  const classes  = useStyles({showFindNearBy});
  const {
    assetName,
    deviceName,
    street,
    state,
    city,
    eventTime,
    status,
    deviceIdValue,
    batteryLevelValue,
    batteryLevelUnits,
    latitude,
    longitude,
    gpsValidity
  } = selectedDevice || {};

  const onNavigation = useCallback((view: NavigationView) => {
    const map = {
      [NavigationView.NAVIGATION] : navigateToGoogleDirections,
      [NavigationView.STREET]     : navigateToGoogleStreetView
    };
    return function() {
      map[view]?.({latitude, longitude});
    };
  }, [selectedDevice]);

  const setToolTip = useCallback(({type}) => {
    setShowHistoryToolTip(!(type === 'click' || type === 'mouseleave'));
  }, []);

  const onReportsClick = useCallback(function() {
    setToolTip({type: 'click'});
    onDeviceReports?.(selectedDevice);
  }, [selectedDevice, setToolTip]);

  const isHistoryEnabled = useIsHistoryAvailable(selectedDevice);

  const addressTooltip = useMemo(() => {
    const isValidGPS = isNonZeroNumber(latitude) && isNonZeroNumber(longitude);
    if (isValidGPS) {
      const title = formatAddress({street, city, state}).toUpperCase();
      const poorGpsTooltip = translate('tracking:list.detailed.poorGpsQuality');
      return (
        <div className={classnames(classes.addressContainer)}>
          {!isTrue(gpsValidity) && (
            <Tooltip arrow
              title={poorGpsTooltip}
              className={classnames(classes.arrow, classes.tooltip)}
              placement={'top'} >
              <span className={classes.iconSpan}>
                <PoorGpsIcon />
              </span>
            </Tooltip>
          )}
          <TooltipWrapper title={title}
            container={getContainer('base-container')}>
            <div className={classes.status}>
              <Address street={street}
                state={state}
                city={city}
                isPopupVariant />
            </div>
          </TooltipWrapper>
        </div>
      );
    }
  }, [street, state, city, latitude, longitude]);

  const isAssetNameAvailable = useMemo(() => !!assetName && assetName?.trim?.().length > 0, [assetName]);
  const name = useMemo(() => {
    if (isAssetNameAvailable) {
      return assetName?.trim?.();
    }
    return deviceName?.trim();
  }, [assetName, isAssetNameAvailable, deviceName]);
  const dateTime = getFormattedDate(eventTime).toUpperCase();
  const statusText = getDeviceStatus(status, selectedDevice as unknown as BaseAsset);
  const statusIcon = getSCStatus(status, selectedDevice as unknown as BaseAsset);
  const assetStatusSvgStyle = useMemo(() => ({
    border       : `1px solid ${dlsColor('white')}`,
    height       : 12,
    width        : 12,
    borderRadius : '50%',
    borderWidth  : '2px'
  }), [mode]);

  return (
    <div className={classes.container}>
      {!showFindNearBy && (
        <div className={classes.contentContainer} onClick={detailView}>
          <div className={classes.statusContainer} style={{backgroundColor: `rgba(${hexToRgb(dlsColor('grey3'))}, 0.8)`}}>
            <div className={classnames(classes.batteryContent, classes.fontContent)}>
              <AssetStatusIcon status={statusIcon} style={assetStatusSvgStyle}/>
              <span>{statusText?.toUpperCase()}</span>
            </div>
            <div className={classnames(classes.batteryContent, classes.fontContent)}>
              <ChargeLevel charge={Number(batteryLevelValue)} />
            </div>
          </div>
          <div className={classes.detailContainer}>
            <BodyTypography variant={BodyTypographyVariantEnum.body2}
              id={'sc-device-popup-device-name'}
              className={classnames(classes.deviceNameContainer, classes.fontContent)}>
              {name}
            </BodyTypography>
            <LabelTypography variantType='label2'
              className={classnames(classes.subText, {
                [classes.hideText] : !isAssetNameAvailable
              })}
              id={'sc-device-popup-device-id'}>
              {deviceName?.trim()}
            </LabelTypography>
            <LabelTypography variantType='label2'
              className={classnames(classes.eventTime, classes.centered)}
              id={'sc-device-popup-device-eventTime'}>
              <SystemDateTimeIcon color={dlsColor('grey1')} />
              {dateTime}
            </LabelTypography>
            {addressTooltip}
          </div>
        </div>)}
      {showFindNearBy && (
        <div className={classes.contentContainer}>
          <FindNearby onFindNearyBy={onFindNearyBy}
            onFindNearbyPopupStatusChange={onFindNearbyPopupStatusChange}
            onFindButtonStateChange={onFindButtonStateChange}
            onFindButtonClick={onFindNearbyClick}
            showFindNearByPopup={isFindNearbyPopup}
            assetData={selectedDevice}
            geofenceCoordinates={findLabel.geofence}
            locationCoordinates={findLabel.location}
            mapView={mapView}
            typeToFindBy={findLabel.type} />
        </div>
      )}
      <div className={classes.actionContainer}>
        <div className={classes.ctaContainer}>
          <div className={classes.centered}>
            <Render.If condition={isHistoryAccessible && isHistoryEnabled}>
              <Tooltip title={translate('tracking:buttons.viewHistory')}
                onMouseEnter={setToolTip}
                onMouseLeave={setToolTip}
                open={showHistoryToolTip}
                disableHoverListener
                arrow>
                <IconButton icon={<SystemViewHistoryIcon />}
                  onClick={onReportsClick}
                  color={dlsColor('caBlue')}
                  size={'small'}
                  labelClass={classnames(classes.CTAlabel)} />
              </Tooltip>
            </Render.If>
          </div>
          <Render.If condition={!isCurrentTheme()}>
            <Tooltip title={translate('tracking:buttons.findNearby')} arrow>
              <IconButton icon={<SystemFindNearestIcon />}
                color={dlsColor('caBlue')}
                size={'small'}
                labelClass={classes.CTAlabel}
                onClick={toggleFindNearbyContent(true)}/>
            </Tooltip>
          </Render.If>
          <Tooltip title={translate('tracking:buttons.navigateTo')} arrow>
            <IconButton icon={<MapLocateIcon />}
              color={dlsColor('caBlue')}
              onClick={onNavigation(NavigationView.NAVIGATION)}
              size={'small'}
              labelClass={classes.CTAlabel} />
          </Tooltip>
          <Tooltip title={translate('tracking:buttons.streetView')} arrow>
            <IconButton icon={<MapPinIcon />}
              onClick={onNavigation(NavigationView.STREET)}
              color={dlsColor('caBlue')}
              size={'small'}
              labelClass={classes.CTAlabel}/>
          </Tooltip>
        </div>
        {showFindNearBy && (
          <IconButton icon={<KeyboardArrowLeftTwoTone />}
            color={dlsColor('caBlue')}
            padding={'4px'}
            onClick={toggleFindNearbyContent(false)}/>
        )}
      </div>
    </div>
  );
};

export default memo(SCDevicePopup);
